import { WORLD } from '@/config';
import { RNG } from '@/utils/rng';

/**
 * The logical city — pure data, no Three.js. Rendering systems mirror this model;
 * the evolution engine mutates it. Keeping the model renderer-agnostic means the
 * whole simulation could run headless (useful for testing evolution behavior).
 */

export enum CellKind {
  /** Latent frontier: never built. The city may grow into it. */
  Void = 0,
  /** A buildable block. May hold one or more buildings. */
  Block = 1,
  /** A through-street / avenue. */
  Street = 2,
  /** An open plaza or garden. */
  Plaza = 3,
}

export type BuildingStyle = 'tower' | 'slab' | 'court' | 'stack' | 'spire' | 'terrace';

export interface Building {
  id: number;
  /** World-space centre of the footprint, on the ground plane. */
  x: number;
  z: number;
  width: number;
  depth: number;
  yaw: number;
  /** Current *target* height in world units. */
  height: number;
  /**
   * Height this building had in the seed city, or 0 if the player's presence
   * caused it (evolution, frontier growth, infill). The finale's "recall" phase
   * collapses every building to this value, physically showing the player the
   * difference between the city they entered and the city they made.
   */
  seedHeight: number;
  /** Reveal progress [0,1]; animated by the renderer when a building is born/raised. */
  growth: number;
  /** Brightness variation [0,1]. */
  tone: number;
  /** Per-building seed for shader variation. */
  seed: number;
  style: BuildingStyle;
  cx: number;
  cz: number;
  /** Assigned instance slot in the render field, or -1 if not yet uploaded. */
  slot: number;
  /** Elapsed time at which this building appeared. */
  bornAt: number;
  /** Marked true when the city decides to remove it (decay). */
  dying: boolean;
}

/**
 * Small set-dressing elements the evolution engine plants so each preference axis
 * has a distinct visual signature: lanterns for intimacy, gates for velocity,
 * trees/stones/pools for contemplation gardens, beacons for verticality/finale.
 */
export type OrnamentKind = 'lantern' | 'gate' | 'tree' | 'stone' | 'beacon' | 'pool';

export interface Ornament {
  id: number;
  kind: OrnamentKind;
  x: number;
  y: number;
  z: number;
  yaw: number;
  scale: number;
  /** Per-ornament seed for shader/motion variation. */
  seed: number;
  /** Reveal progress [0,1]; animated by the renderer. */
  growth: number;
  /** Assigned instance slot in the render field, or -1. */
  slot: number;
}

export interface SkyLink {
  id: number;
  /** Endpoints in world space (rooftop to rooftop, or floating platform). */
  ax: number;
  ay: number;
  az: number;
  bx: number;
  by: number;
  bz: number;
  width: number;
  growth: number;
  slot: number;
  kind: 'bridge' | 'platform';
}

export class City {
  readonly grid = WORLD.grid;
  readonly cellSize = WORLD.cellSize;
  private kinds: Uint8Array;
  readonly buildings: Building[] = [];
  readonly links: SkyLink[] = [];
  readonly ornaments: Ornament[] = [];

  /** Current built radius in cells; grows via the evolution engine. */
  builtRadius: number = WORLD.initialRadius;
  /** City maturation [0,1]; the narrative arc completes at 1. */
  maturity = 0;
  /** Aliveness [0,1]; falls when the player stagnates, drives the mood system. */
  vitality = 0.6;

  private nextBuildingId = 1;
  private nextLinkId = 1;
  private nextOrnamentId = 1;

  constructor(readonly rng: RNG) {
    this.kinds = new Uint8Array(this.grid * this.grid).fill(CellKind.Void);
  }

  /* -------- grid helpers -------- */

  inBounds(cx: number, cz: number): boolean {
    return cx >= 0 && cz >= 0 && cx < this.grid && cz < this.grid;
  }
  private idx(cx: number, cz: number): number {
    return cz * this.grid + cx;
  }
  kindAt(cx: number, cz: number): CellKind {
    if (!this.inBounds(cx, cz)) return CellKind.Void;
    return this.kinds[this.idx(cx, cz)];
  }
  setKind(cx: number, cz: number, kind: CellKind): void {
    if (this.inBounds(cx, cz)) this.kinds[this.idx(cx, cz)] = kind;
  }

  /** Grid centre (fractional cell coordinate). */
  get centre(): number {
    return (this.grid - 1) / 2;
  }

  /** Convert a cell coordinate to the world-space position of its centre. */
  cellToWorld(cx: number, cz: number): { x: number; z: number } {
    return {
      x: (cx - this.centre) * this.cellSize,
      z: (cz - this.centre) * this.cellSize,
    };
  }

  worldToCell(x: number, z: number): { cx: number; cz: number } {
    return {
      cx: Math.round(x / this.cellSize + this.centre),
      cz: Math.round(z / this.cellSize + this.centre),
    };
  }

  /** Chebyshev distance in cells from the centre. */
  ringOf(cx: number, cz: number): number {
    return Math.max(Math.abs(cx - this.centre), Math.abs(cz - this.centre));
  }

  /* -------- mutation -------- */

  addBuilding(
    b: Omit<Building, 'id' | 'slot' | 'growth' | 'dying' | 'seedHeight'> & { seedHeight?: number },
  ): Building {
    const building: Building = {
      // seedHeight defaults to 0: unless a caller says otherwise, a building is
      // something the player's presence caused, and the finale recalls it away.
      seedHeight: 0,
      ...b,
      id: this.nextBuildingId++,
      slot: -1,
      growth: 0,
      dying: false,
    };
    this.buildings.push(building);
    return building;
  }

  addLink(l: Omit<SkyLink, 'id' | 'slot' | 'growth'>): SkyLink {
    const link: SkyLink = { ...l, id: this.nextLinkId++, slot: -1, growth: 0 };
    this.links.push(link);
    return link;
  }

  addOrnament(o: Omit<Ornament, 'id' | 'slot' | 'growth'>): Ornament {
    const orn: Ornament = { ...o, id: this.nextOrnamentId++, slot: -1, growth: 0 };
    this.ornaments.push(orn);
    return orn;
  }

  /** All buildings whose centre lies within `radiusCells` rings of the centre. */
  buildingsInRing(minRing: number, maxRing: number): Building[] {
    return this.buildings.filter((b) => {
      const r = this.ringOf(b.cx, b.cz);
      return r >= minRing && r <= maxRing && !b.dying;
    });
  }

  /** The tallest buildings, for skybridge/rooftop candidate selection. */
  tallest(count: number): Building[] {
    return [...this.buildings]
      .filter((b) => !b.dying)
      .sort((a, b) => b.height - a.height)
      .slice(0, count);
  }

  /* -------- serialization -------- */

  exportState(): CityState {
    return {
      kinds: Array.from(this.kinds),
      buildings: this.buildings
        .filter((b) => !b.dying)
        .map((b) => ({ ...b, slot: -1, growth: Math.max(b.growth, 0.999) })),
      links: this.links.map((l) => ({ ...l, slot: -1, growth: Math.max(l.growth, 0.999) })),
      ornaments: this.ornaments.map((o) => ({ ...o, slot: -1, growth: Math.max(o.growth, 0.999) })),
      builtRadius: this.builtRadius,
      maturity: this.maturity,
      vitality: this.vitality,
      nextBuildingId: this.nextBuildingId,
      nextLinkId: this.nextLinkId,
      nextOrnamentId: this.nextOrnamentId,
    };
  }

  importState(state: CityState): void {
    this.kinds = Uint8Array.from(state.kinds);
    this.buildings.length = 0;
    for (const b of state.buildings) {
      this.buildings.push({ ...b, seedHeight: b.seedHeight ?? 0, slot: -1 });
    }
    this.links.length = 0;
    for (const l of state.links) this.links.push({ ...l, slot: -1 });
    this.ornaments.length = 0;
    for (const o of state.ornaments ?? []) this.ornaments.push({ ...o, slot: -1 });
    this.builtRadius = state.builtRadius;
    this.maturity = state.maturity;
    this.vitality = state.vitality;
    this.nextBuildingId = state.nextBuildingId;
    this.nextLinkId = state.nextLinkId;
    this.nextOrnamentId = state.nextOrnamentId ?? 1;
  }
}

export interface CityState {
  kinds: number[];
  buildings: Building[];
  links: SkyLink[];
  ornaments: Ornament[];
  builtRadius: number;
  maturity: number;
  vitality: number;
  nextBuildingId: number;
  nextLinkId: number;
  nextOrnamentId: number;
}
